// Stefans-AI-CV-frontend/src/sections/About.tsx

import { motion } from 'framer-motion'
import { useI18n } from '../i18n'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

export default function About() {
  const t = useI18n()

  const tags = [
    { key: 'tag_web3', label: t.about.tag_web3, color: 'cyan' },
    { key: 'tag_quant', label: t.about.tag_quant, color: 'indigo' },
    { key: 'tag_ai', label: t.about.tag_ai, color: 'cyan' },
    { key: 'tag_research', label: t.about.tag_research, color: 'indigo' },
    { key: 'tag_product', label: t.about.tag_product, color: 'cyan' },
    { key: 'tag_teaching', label: t.about.tag_teaching, color: 'indigo' },
  ]

  return (
    <section id="about" className="py-24 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-cyan-400 text-sm font-mono mb-2">// about</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-3">
            {t.about.title}
          </h2>
          <p className="text-text-secondary max-w-2xl">{t.about.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-start mb-16">
          {/* Left: Text content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="space-y-5"
          >
            {[t.about.p1, t.about.p2, t.about.p3].map((para, i) => (
              <motion.p
                key={i}
                variants={itemVariants}
                className="text-text-secondary leading-relaxed text-base"
              >
                {para}
              </motion.p>
            ))}
          </motion.div>

          {/* Right: Skill tags + decorative block */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Skills tag cloud */}
            <div className="glass-card rounded-2xl p-6">
              <p className="text-text-muted text-xs font-mono mb-4 uppercase tracking-wider">Core Expertise</p>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag.key}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 cursor-default hover:scale-105 ${
                      tag.color === 'cyan'
                        ? 'bg-cyan-dim border-cyan-400/20 text-cyan-400 hover:border-cyan-400/40'
                        : 'bg-indigo-dim border-indigo-400/20 text-indigo-400 hover:border-indigo-400/40'
                    }`}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Quote block */}
            <div className="glass-card rounded-2xl p-6 border-l-2 border-cyan-400/40">
              <div className="text-4xl text-cyan-400/30 font-serif leading-none mb-3">"</div>
              <p className="text-text-secondary text-sm leading-relaxed italic">
                {t.about.p2.slice(0, 80)}...
              </p>
              <div className="mt-4 flex items-center gap-2">
                <div className="w-6 h-0.5 bg-cyan-400/40" />
                <span className="text-text-muted text-xs font-mono">Stefans</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* 4-capability map */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-cyan-400 text-sm font-mono mb-6">{t.about.capabilities_title}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(t.about.capabilities as { icon: string; title: string; desc: string }[]).map((cap, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass-card rounded-2xl p-5 group hover:border-border-strong hover:-translate-y-1 transition-all duration-300"
              >
                <div className="text-2xl mb-3">{cap.icon}</div>
                <h4 className="font-heading font-semibold text-text-primary text-sm mb-2 group-hover:text-cyan-400 transition-colors">
                  {cap.title}
                </h4>
                <p className="text-text-muted text-xs leading-relaxed">{cap.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
